import {
  forwardRef,
  useCallback,
  useEffect,
  useLayoutEffect,
  useContext,
} from "react";
import { Editor } from "@tiptap/react";
import { FormProvider, useForm } from "react-hook-form";
import { object, string } from "yup";
import cx from "classnames";

import { Button } from "@/components/ui/button";
import { useEscapeKey } from "@/hooks/useEscapeKey";
import { sharedToolbarCss } from "../Toolbar";
import { useSecondaryFloatingPanelsStore } from "../Toolbar/context";

import { linkPanelContext } from "./context";

type LinkPanelProps = {
  href?: string | null;
  editor: Editor;
  onLinkSet: () => void;
};

const schema = object({
  link: string().url().required(),
}).required();

export const LinkPanel = forwardRef<HTMLFormElement, LinkPanelProps>(
  ({ editor, href, onLinkSet }, ref) => {
    const { refs, style } = useContext(linkPanelContext);
    const { isSecondaryPanelDisplayed, setIsSecondaryPanelDisplayed } =
      useSecondaryFloatingPanelsStore();
    const formCtx = useForm({ defaultValues: { link: "" } });
    const linkValue = formCtx.watch("link");

    useEscapeKey(refs!.floating, () => {
      onLinkSet();

      if (isSecondaryPanelDisplayed) {
        setIsSecondaryPanelDisplayed(false);
      }

      if (!href) {
        editor.chain().focus().run();
      } else {
        const pos = editor.state.selection.from;

        editor.chain().focus().setTextSelection({ from: pos, to: pos }).run();
      }
    });

    useEffect(() => setIsSecondaryPanelDisplayed(true), []);

    useLayoutEffect(() => {
      setTimeout(() => formCtx.setFocus("link"), 10);
    }, [formCtx]);

    useEffect(() => {
      if (!!href) {
        formCtx.setValue("link", href);
      }
    }, [href, formCtx]);

    const onSubmit = (data: any) => {
      const pos = editor.state.selection.from;

      editor
        .chain()
        .focus()
        .setLink({ href: data.link })
        .setTextSelection({ from: pos, to: pos })
        .run();
      onLinkSet();
    };

    const validateLink = useCallback((value: string) => {
      const isValid = schema.isValidSync({ link: value });
      return isValid;
    }, []);

    return (
      <FormProvider {...formCtx}>
        <form
          ref={ref}
          style={{ ...style }}
          onSubmit={formCtx.handleSubmit(onSubmit)}
          className={cx(
            sharedToolbarCss,
            "w-72 px-2 fixed z-20 ring ring-gray-400/50 ring-opacity-50 has-focus-within:shadow-xl"
          )}
        >
          <input
            type="text"
            placeholder="Your link"
            {...formCtx.register("link")}
            autoComplete="off"
            className="w-full h-6 outline-none bg-transparent border-none text-sm text-gray-100 px-0 ring-0"
          />
          {linkValue !== "" && linkValue.length > 0 && (
            <Button
              variant="outline"
              type="submit"
              size="xs"
              className="w-full my-1"
            >
              {!!href ? "Modify link" : "Add link"}
            </Button>
          )}
        </form>
      </FormProvider>
    );
  }
);
